/**
 * Onboarding Tour Component
 * Interactive guide for new users showing the FinList journey
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  ArrowRight, 
  Home, 
  ShoppingCart, 
  Check, 
  TrendingUp, 
  BarChart, 
  Target,
  Sparkles
} from 'lucide-react';
import { 
  shouldShowOnboarding, 
  dismissOnboarding, 
  getNextStepSuggestion,
  JOURNEY_MILESTONES
} from '@/services/journeyService';

export default function OnboardingTour() {
  const [showTour, setShowTour] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const slides = [
    {
      title: '👋 Welcome to FinList!',
      description: 'Your privacy-first personal finance companion',
      icon: Sparkles,
      color: '#8b5cf6',
      details: 'Track expenses, manage shopping lists, and achieve your financial goals - all in one place.',
      cta: 'Get Started'
    },
    {
      title: '🏠 Dashboard Overview',
      description: 'See your financial health at a glance',
      icon: Home,
      color: '#006D77',
      details: 'View your balance, income, expenses, and quick access to all features.',
      cta: 'Next',
      route: '/dashboard'
    },
    {
      title: '🛒 Smart Shopping Lists',
      description: 'Create lists and track purchases',
      icon: ShoppingCart,
      color: '#06D6A0',
      details: 'Add items to your shopping list. When you mark them as bought, expenses are automatically tracked!',
      cta: 'Next',
      route: '/shopping-lists'
    },
    {
      title: '✅ Auto Expense Tracking',
      description: 'Purchases become expenses automatically',
      icon: Check,
      color: '#10b981',
      details: 'When you check off items as bought, FinList creates expense transactions for you - saving time!',
      cta: 'Next'
    },
    {
      title: '💰 Track Your Income',
      description: 'Log income from any source',
      icon: TrendingUp,
      color: '#FFD60A',
      details: 'Add salary, freelance earnings, or any income to see your complete financial picture.',
      cta: 'Next'
    },
    {
      title: '📈 Visual Insights',
      description: 'Understand your spending patterns',
      icon: BarChart,
      color: '#118AB2',
      details: 'Insights merged into Budget; view trends and controls there.',
      cta: 'Next',
      route: '/budget'
    },
    {
      title: '🎯 Savings Goals',
      description: 'Set goals and track progress',
      icon: Target,
      color: '#EF476F',
      details: 'Create savings goals, add contributions, and watch your progress with visual trackers.',
      cta: 'Start Your Journey!',
      route: '/savings-goals'
    }
  ];

  useEffect(() => {
    checkOnboarding();
  }, []);

  async function checkOnboarding() {
    const shouldShow = await shouldShowOnboarding();
    setShowTour(shouldShow);
  }

  function handleClose() {
    dismissOnboarding();
    setShowTour(false);
  }

  function handleNext() {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      // Last slide - navigate to dashboard and close
      handleClose();
      navigate('/dashboard');
    }
  }

  function handleSkip() {
    handleClose();
    navigate('/dashboard');
  }

  if (!showTour) return null;

  const slide = slides[currentSlide];
  const Icon = slide.icon;
  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${slide.color}15` }}
              >
                <Icon className="w-6 h-6" style={{ color: slide.color }} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{slide.title}</h2>
                <p className="text-sm text-gray-500">
                  Step {currentSlide + 1} of {slides.length}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-8">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div 
                className="w-20 h-20 rounded-3xl mx-auto mb-6 flex items-center justify-center"
                style={{ backgroundColor: `${slide.color}15` }}
              >
                <Icon className="w-10 h-10" style={{ color: slide.color }} />
              </div>

              <h3 className="text-2xl font-bold text-gray-900 text-center mb-3">
                {slide.description}
              </h3>

              <p className="text-gray-600 text-center leading-relaxed mb-6">
                {slide.details}
              </p>

              {/* Progress dots */}
              <div className="flex items-center justify-center gap-2 mb-8">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 rounded-full transition-all ${
                      index === currentSlide
                        ? 'w-8'
                        : 'w-2'
                    }`}
                    style={{
                      backgroundColor: index === currentSlide 
                        ? slide.color 
                        : '#E5E7EB'
                    }}
                  />
                ))}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {!isLastSlide && (
                  <button
                    onClick={handleSkip}
                    className="flex-1 py-3 px-6 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                  >
                    Skip Tour
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="flex-1 py-3 px-6 rounded-xl font-semibold text-white flex items-center justify-center gap-2 hover:shadow-lg transition-all"
                  style={{ backgroundColor: slide.color }}
                >
                  {slide.cta}
                  {!isLastSlide && <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
